import { useEffect, useMemo, useState } from "react";
import { BACKEND_AI_AGENT_URL } from "../../../../config";
import DropDown from "../../../../components/DropDown/DropDown";
import {
  abbreviatePrice,
  capitalizeFirstLetter,
} from "../../../../utils/utils";

const ProgressBar = ({ stages }) => {
  const totalWidth = stages.reduce((acc, stage) => acc + stage.width, 0);

  return (
    <div className="w-full flex gap-2">
      {stages.map((stage, index) => {
        const proportionalWidth = (stage.width / totalWidth) * 100;
        const width = proportionalWidth < 3 ? 3 : proportionalWidth;

        return (
          <div
            key={index}
            className={`h-[6px] rounded-full flex-grow ${stage.color}`}
            style={{ width: `${width}%`, minWidth: "3%" }}
          ></div>
        );
      })}
    </div>
  );
};

interface Project {
  projectId: string;
  projectsNarratives: string;
  marketCap: number;
  percentageChange: number;
  percentageDominance: number;
  symbol: string;
}

interface DominanceData {
  groupedByNarratives: {
    narrative: string;
    projects: Project[];
  }[];
}

export const Dominance = () => {
  const [value, setValue] = useState<DominanceData>({
    groupedByNarratives: [],
  });
  const [selectedValue, setSelectedValue] = useState<string>("");

  useEffect(() => {
    const fetchTrendingData = async () => {
      try {
        const response = await fetch(`${BACKEND_AI_AGENT_URL}/dominance`);

        if (!response.ok) {
          console.error("Failed to fetch fear and greed index");
          return;
        }

        const data: DominanceData = await response.json();
        setValue(data);
      } catch (error) {
        console.error("Failed to fetch dominance narratives:", error);
      }
    };

    fetchTrendingData();
  }, []);

  const options = useMemo(() => {
    return value.groupedByNarratives
      .filter((item) => item.projects.length > 2)
      .map((group) => ({
        label: group.narrative,
        value: group.narrative,
      }));
  }, [value.groupedByNarratives]);

  const projectsList = useMemo(() => {
    const result = value.groupedByNarratives?.find(
      (project) =>
        project.narrative ===
        (selectedValue ? selectedValue : options[0]?.value),
    )?.projects;

    return result || [];
  }, [options, selectedValue, value.groupedByNarratives]);

  const progressBarStages = useMemo(() => {
    return [
      {
        color: "bg-blue-500",
        width: projectsList[0]?.percentageDominance || 0,
      },
      {
        color: "bg-blue-300",
        width: projectsList[1]?.percentageDominance || 0,
      },
      {
        color: "bg-gray-500",
        width: projectsList[2]?.percentageDominance || 0,
      },
    ];
  }, [projectsList]);

  return (
    <div>
      <div className="flex items-center gap-2 font-bold">
        <span>Dominance</span>
        <span className="flex flex-1">
          <DropDown
            value={selectedValue || options[0]?.value}
            size="SMALL"
            options={options}
            placeholder="Chain"
            onClick={setSelectedValue}
            className="w-full"
          />
        </span>
        <span>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 16V9H16V16H12ZM6 16V0H10V16H6ZM0 16V5H4V16H0Z"
              fill="#007DFF"
            />
          </svg>
        </span>
      </div>
      <div className="text-xs opacity-50 mb-2">Market cap</div>

      <div className="flex justify-between mb-1">
        {projectsList.map((data: Project) => (
          <div key={data.projectId} className="flex flex-col gap-[2px]">
            <div className="flex items-center gap-1">
              <img
                src={`https://mygateway.io/assets/img/tokens/${data.symbol}.webp`}
                alt="doge"
                className="w-4 h-4"
              />
              <div className="text-xs">
                {capitalizeFirstLetter(data.projectId)}
              </div>
            </div>
            <div className="text-xs">
              {abbreviatePrice(data.marketCap).toUpperCase()} USD
            </div>
            <div className="text-[10px]">
              <span className="opacity-50">24h: </span>
              <span
                className={`opacity-1 ${
                  data.percentageChange > 0
                    ? "text-[#4CAF50]"
                    : "text-[#F44336]"
                } `}
              >
                {data.percentageChange.toFixed(2)}%
              </span>
            </div>
          </div>
        ))}
      </div>
      <ProgressBar stages={progressBarStages} />
    </div>
  );
};
